import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import api from '../config/api';
import { DOCUMENT } from '../constants/documentTheme';
import MemoriesAlbumView from './MemoriesAlbumView';

const TripMemoriesPanel = ({ trip, isCreator }) => {
  const { t } = useTranslation();
  const [event, setEvent] = useState(undefined);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!trip?.id) return undefined;
    let cancelled = false;
    api.get(`/memories/trips/${trip.id}/event`).then(({ data }) => {
      if (!cancelled) setEvent(data.event);
    }).catch(() => {
      if (!cancelled) setEvent(null);
    });
    return () => { cancelled = true; };
  }, [trip?.id]);

  const createForTrip = async () => {
    setCreating(true);
    setError('');
    try {
      const { data } = await api.post('/memories/events', { tripId: trip.id });
      setEvent(data.event);
    } catch (err) {
      setError(err.response?.data?.error || t('memories.createFailed'));
    } finally {
      setCreating(false);
    }
  };

  if (event === undefined) {
    return <CircularProgress size={24} sx={{ color: DOCUMENT.oxblood }} />;
  }

  if (event?.slug) {
    return <MemoriesAlbumView slug={event.slug} embedded />;
  }

  return (
    <Box
      sx={{
        backgroundColor: DOCUMENT.surface,
        border: `1px solid ${DOCUMENT.rule}`,
        p: 2.5,
      }}
    >
      <Typography sx={{ letterSpacing: '0.16em', textTransform: 'uppercase', fontSize: 11, color: DOCUMENT.accent, mb: 1 }}>
        {t('memories.brand')}
      </Typography>
      <Typography sx={{ fontFamily: DOCUMENT.display, fontSize: 26, color: DOCUMENT.ink, mb: 1 }}>
        {t('memories.cardTitle')}
      </Typography>
      <Typography sx={{ color: DOCUMENT.ink3, mb: 2 }}>
        {isCreator ? t('memories.cardCreateHint') : t('memories.tripTabEmpty')}
      </Typography>
      {error && <Typography sx={{ color: '#9C3B2A', mb: 2 }}>{error}</Typography>}
      {isCreator && (
        <Button
          onClick={createForTrip}
          disabled={creating}
          sx={{
            backgroundColor: DOCUMENT.oxblood,
            color: DOCUMENT.ivory,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            fontSize: 11,
            borderRadius: '2px',
            '&:hover': { backgroundColor: '#5C1A22' },
          }}
        >
          {t('memories.createFromTrip')}
        </Button>
      )}
    </Box>
  );
};

export default TripMemoriesPanel;
